<script setup lang="ts">
import type UiAccordion from '@afs/design-system/components/molecules/UiAccordion/UiAccordion.vue.d.ts'
import { UiAccordionVariant, UiHighlightVariant, UiHighlightColor } from '@afs/design-system'

import { useI18n } from 'vue-i18n'
import { onMounted, ref } from 'vue'
import { navigate } from 'vike/client/router'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted, useGtmSimulatorClick } from '@/composables/useGtmTag'
import { openLegalNotice, scrollToLegalNotice } from '@/composables/useAccordion'
import type { ParentHomeData } from '@/pages/(app)/index/+data'
import { useSimulation } from '@/composables/useSimulation'

const props = withDefaults(defineProps<Props>(), {
  data: () => <ParentHomeData>{},
})

const { t } = useI18n()

const legalNoticeAccordion = ref<InstanceType<typeof UiAccordion> | null>(null)
onMounted(() => openLegalNotice(legalNoticeAccordion.value))

type Props = {
  data: ParentHomeData
}

if (props.data && props.data.homepage) {
  useFormatSeoContent(props.data.homepage.seoTitle, props.data.homepage.hero.surtitle)
  useGtmOnPageMounted('home')
}
const { additionnalLegalProps } = useSimulation(props.data?.homepage)

const trackSubmit = useGtmSimulatorClick()

const getHighlightVariant = (index: number) => {
  if (
    props.data.homepage &&
    props.data.homepage?.promotionalBlocks &&
    props.data.homepage?.promotionalBlocks.length > 1
  ) {
    return index === 0 ? UiHighlightVariant.FLAT : UiHighlightVariant.OUTLINE
  } else {
    return undefined
  }
}

const getHighlightColor = (index: number) => {
  if (
    props.data.homepage &&
    props.data.homepage?.promotionalBlocks &&
    props.data.homepage?.promotionalBlocks.length > 1
  ) {
    return index === 0 ? UiHighlightColor.GREEN_03 : undefined
  } else {
    return undefined
  }
}

const onSimulatorSubmit = (payload: string | number | null, event?: Event) => {
  if (typeof payload === 'string') return

  if (event && event.type === 'submit') {
    const submitEvent = event as SubmitEvent

    if (submitEvent.submitter?.innerText) {
      trackSubmit(submitEvent.submitter.innerText)
    }
  }

  const searchParams = new URLSearchParams()
  if (payload) searchParams.set('amount', payload?.toString())
  navigate(`/simulateur?${searchParams}`)
}

const heroSimulatordProps = props.data.homepage?.hero.simulator
  ? {
      ...props.data.homepage.hero.simulator,
      id: 'simulator',
      action: onSimulatorSubmit,
    }
  : undefined

const heroPromotionProps = props.data.homepage?.hero?.promotion
  ? {
      ...props.data.homepage.hero.promotion,
      legalNoticeButtonLabel: t('homepage.mentionLegal.buttonLabel'),
    }
  : undefined

const legalNotice = {
  id: 'homepage-legal-notice',
  title: t('homepage.mentionLegal.title'),
}
</script>

<template>
  <div
    v-if="data.homepage"
    class="Homepage"
  >
    <organisms-ui-hero
      v-if="props.data.homepage"
      v-bind="props.data.homepage?.hero"
      :promotion="heroPromotionProps"
      :simulator="heroSimulatordProps"
      class="Hero"
      @hero::on-legal-notice-click="scrollToLegalNotice(legalNoticeAccordion)"
    />
    <div
      v-if="data.homepage.loanExampleBlock"
      class="container"
    >
      <organisms-ui-credit-example
        v-if="props.data.homepage && props.data.homepage?.loanExampleBlock"
        :image="props.data.homepage?.loanExampleBlock.image"
        :illustration-left="props.data.homepage?.loanExampleBlock.illustrationLeft"
        :illustration-right="props.data.homepage?.loanExampleBlock.illustrationRight"
        :text="props.data.homepage?.loanExampleBlock.text"
        :cta="props.data.homepage?.loanExampleBlock.cta"
        class="my-7"
      />
    </div>
    <organisms-ui-loan-types
      v-if="props.data.homepage && props.data.homepage.loanTypes"
      v-bind="props.data.homepage.loanTypes"
    />
    <div
      v-if="props.data.homepage"
      class="container my-8"
    >
      <section class="Homepage__promotional grid mb-7">
        <molecules-ui-highlight
          v-for="(promotion, index) in props.data.homepage.promotionalBlocks"
          :key="promotion.id"
          v-bind="promotion"
          :variant="getHighlightVariant(index)"
          :color="getHighlightColor(index)"
        />
      </section>
      <section v-if="props.data.homepage.reinsuranceBlock">
        <molecules-ui-arguments :arguments="props.data.homepage.reinsuranceBlock" />
      </section>
    </div>
    <organisms-ui-steps
      v-if="props.data.homepage && props.data.homepage.stepsBlock"
      class="mb-6"
      :title="props.data.homepage.stepsBlock.title"
      :steps="props.data.homepage.stepsBlock.steps"
      :cta="props.data.homepage.stepsBlock.cta"
    />
    <organisms-ui-offers
      v-if="props.data.homepage && props.data.homepage.offersBlock"
      :title="props.data.homepage.offersBlock.title"
      :sub-title="props.data.homepage.offersBlock.subTitle"
      :offers="props.data.homepage.offersBlock.offers"
    />
    <organisms-ui-help
      v-if="props.data.homepage && props.data.homepage.faqBlock && props.data.homepage.faqList"
      :title="props.data.homepage.faqBlock.title"
      :contact="props.data.homepage.faqBlock.contact"
      :image="props.data.homepage.faqBlock.image"
    >
      <molecules-ui-accordion
        :items="props.data.homepage.faqList"
        :variant="UiAccordionVariant.SECONDARY"
        name="homepage-faq"
      >
        <template
          v-for="question in props.data.homepage.faqList"
          :key="question.id"
          #[question.id]
        >
          <h3
            v-if="question.subtitle"
            class="mb-2 heading-4"
          >
            {{ question.subtitle }}
          </h3>
          <molecules-ui-rich-text :content="question.content" />
        </template>
      </molecules-ui-accordion>
    </organisms-ui-help>
    <div class="container">
      <section
        v-if="props.data.homepage"
        class="grid"
      >
        <molecules-ui-brand
          v-if="props.data.homepage.brandBlock"
          class="Homepage__brand my-7"
          v-bind="props.data.homepage.brandBlock"
        />
        <molecules-ui-accordion
          ref="legalNoticeAccordion"
          class="Homepage__legalNotice mt-4 mb-8"
          :items="[legalNotice]"
        >
          <template
            v-for="item in [legalNotice]"
            :key="item.id"
            #[item.id]
          >
            <molecules-ui-rich-text
              v-if="props.data.homepage && additionnalLegalProps"
              class="mt-3"
              :content="additionnalLegalProps"
            />
            <molecules-ui-rich-text
              v-if="props.data.homepage.legalNoticeBlock.legalNotice"
              class="mt-3"
              :content="props.data.homepage.legalNoticeBlock.legalNotice"
            />
          </template>
        </molecules-ui-accordion>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/spaces';
@use 'utilities/mq';

.Homepage__promotional {
  gap: map.get(spaces.$sizes, '7');

  @include mq.media('>=medium') {
    gap: map.get(spaces.$sizes, '5');
  }

  :deep(.Highlight) {
    grid-column: span 4;

    @include mq.media('>=medium') {
      &:first-child {
        grid-column: span 5;
      }

      &:last-child {
        grid-column: span 7;
      }

      &:only-child {
        grid-column: 2 / span 10;
      }
    }
  }
}

.Homepage__brand,
.Homepage__legalNotice {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 10;
  }
}

.Homepage__legalNotice {
  --scroll-offset: 50px;

  @include mq.media('>=medium') {
    --scroll-offset: 200px;
  }

  &:deep(.Accordion__item) {
    scroll-margin: calc(var(--header-height) + var(--scroll-offset));
  }
}
</style>
